import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Autocomplete } from "@base-ui/react";
import get from "lodash/get";

import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxList,
} from "@/components/ui/combobox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { InputGroupAddon } from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";
import { parsePlaceAddress } from "components/forms/places/parsePlaceAddress";
import PlacesProvider from "components/forms/places/PlacesProvider";
import {
  fetchPlaceDetails,
  usePlacePredictions,
} from "components/forms/places/usePlacePredictions";
import { usePlacesServices } from "components/forms/places/usePlacesServices";

import { REQUIRED_MARK } from "./fieldClasses";

const ITEM_CLASSES =
  "tw:relative tw:flex tw:w-full tw:cursor-default tw:items-center tw:gap-2 tw:rounded-md tw:py-1 tw:px-1.5 tw:text-sm tw:outline-hidden tw:select-none tw:data-highlighted:bg-accent tw:data-highlighted:text-accent-foreground";

const ADDRESS_FIELDS = [
  "street1",
  "street2",
  "city",
  "region",
  "postal_code",
  "country",
];

const placeLatLng = info => {
  const location = info?.geometry?.location;
  if (!location) return null;
  // A fresh PlaceResult exposes lat() and lng(); a saved one round-trips as
  // plain { lat, lng }.
  return typeof location.lat === "function"
    ? { lat: location.lat(), lng: location.lng() }
    : location;
};

// The coordinates are place-derived only while they still match the point the
// place describes, so coordinates the user typed by hand are never cleared.
const looksPlaceDerived = address => {
  const location = placeLatLng(address.info);
  return (
    !!location &&
    Number(address.latitude) === Number(location.lat) &&
    Number(address.longitude) === Number(location.lng)
  );
};

const toInputValue = coordinate =>
  coordinate == null ? "" : String(coordinate);

const StreetField = ({
  name,
  label,
  required,
  placeholder = "123 Main St",
  disabled,
  id,
}) => {
  const {
    control,
    getValues,
    setValue,
    formState: { errors, isSubmitted },
  } = useFormContext();
  const streetName = `${name}.street1`;
  const error = get(errors, streetName);
  const inputId = id ?? streetName;

  const { services } = usePlacesServices();
  const [query, setQuery] = useState("");
  const country = useWatch({ name: `${name}.country` });
  const { data: predictions = [], isSearching } = usePlacePredictions({
    services,
    query,
    country,
  });
  const [isResolvingPlace, setIsResolvingPlace] = useState(false);
  const isBusy = isSearching || isResolvingPlace;
  // Autocomplete reports a picked item only as its text, so the item under
  // the pointer or the keyboard is kept to know which place was picked.
  const highlightedRef = useRef(null);

  const setField = (field, value) =>
    setValue(`${name}.${field}`, value, {
      shouldDirty: true,
      shouldValidate: isSubmitted,
    });

  const fillFromPlace = async prediction => {
    setIsResolvingPlace(true);
    try {
      const place = parsePlaceAddress(
        await fetchPlaceDetails(services, prediction.placeId),
        prediction.street
      );
      ADDRESS_FIELDS.forEach(field => setField(field, place[field]));
      setField("latitude", toInputValue(place.latitude));
      setField("longitude", toInputValue(place.longitude));
      setField("info", place.info);
    } catch {
      // The street is already set, and the user completes the rest by hand.
    } finally {
      setIsResolvingPlace(false);
    }
  };

  const handleStreetChange = (field, text) => {
    // A street changed by hand away from the place its coordinates describe
    // drops the now-stale geocode and its place data.
    if (text !== field.value && looksPlaceDerived(getValues(name))) {
      setField("latitude", "");
      setField("longitude", "");
      setField("info", null);
    }
    field.onChange(text);
  };

  return (
    <Controller
      name={streetName}
      control={control}
      render={({ field }) => (
        <Field data-invalid={error ? true : undefined}>
          {label && (
            <FieldLabel
              htmlFor={inputId}
              className={required ? REQUIRED_MARK : undefined}
            >
              {label}
            </FieldLabel>
          )}
          <Autocomplete.Root
            items={predictions}
            value={field.value ?? ""}
            onValueChange={(text, details) => {
              handleStreetChange(field, text);
              if (details.reason === "item-press" && highlightedRef.current) {
                fillFromPlace(highlightedRef.current);
              } else {
                setQuery(text);
              }
            }}
            onItemHighlighted={prediction => {
              highlightedRef.current = prediction ?? null;
            }}
            itemToStringValue={prediction => prediction.street}
            filter={null}
            disabled={disabled}
          >
            <ComboboxInput
              id={inputId}
              placeholder={placeholder}
              showTrigger={false}
              // Keeps password managers (1Password, LastPass, Bitwarden) from
              // offering to fill the street, as FormInput does.
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-bwignore
              data-form-type="other"
              disabled={disabled}
              aria-required={required || undefined}
              aria-invalid={error ? true : undefined}
              aria-busy={isBusy || undefined}
              onBlur={field.onBlur}
            >
              {isBusy ? (
                <InputGroupAddon align="inline-end">
                  <Spinner />
                </InputGroupAddon>
              ) : null}
            </ComboboxInput>
            <ComboboxContent>
              <ComboboxEmpty>
                {query.trim() && !isSearching ? "No matching addresses" : null}
              </ComboboxEmpty>
              <ComboboxList>
                {prediction => (
                  <Autocomplete.Item
                    key={prediction.placeId}
                    value={prediction}
                    className={ITEM_CLASSES}
                  >
                    {prediction.label}
                  </Autocomplete.Item>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Autocomplete.Root>
          <FieldError>{error?.message}</FieldError>
        </Field>
      )}
    />
  );
};

/**
 * Street 1 of an address, with Google Places suggestions. `name` is the path
 * of the address object, such as `addresses.0`. The field edits its
 * `street1`, and picking a suggestion fills the rest of the address. Typed
 * text is kept as is, so the field also works when Places is unavailable.
 */
const FormAddressStreet = props => (
  <PlacesProvider>
    <StreetField {...props} />
  </PlacesProvider>
);

const propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

StreetField.propTypes = propTypes;
FormAddressStreet.propTypes = propTypes;

export default FormAddressStreet;
